// Register Page - Premium Design
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Route, Mail, Lock, User, Loader2, Eye, EyeOff, Car, Truck, Users, Briefcase, ChevronRight, Check, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const PROFESSIONS = [
  { id: 'vtc', label: 'VTC / Taxi', icon: Car, description: 'Transport de passagers' },
  { id: 'delivery', label: 'Livreur', icon: Truck, description: 'Livraison et coursier' },
  { id: 'fleet', label: 'Flotte', icon: Users, description: 'Gestion multi-véhicules' },
  { id: 'independent', label: 'Indépendant', icon: Briefcase, description: 'Freelance, consultant' },
];

const COUNTRIES = [
  { code: 'FR', name: 'France', flag: '🇫🇷', unit: 'km', currency: 'EUR' },
  { code: 'US', name: 'États-Unis', flag: '🇺🇸', unit: 'miles', currency: 'USD' },
  { code: 'GB', name: 'Royaume-Uni', flag: '🇬🇧', unit: 'miles', currency: 'GBP' },
  { code: 'ES', name: 'Espagne', flag: '🇪🇸', unit: 'km', currency: 'EUR' },
  { code: 'MA', name: 'Maroc', flag: '🇲🇦', unit: 'km', currency: 'MAD' },
  { code: 'SA', name: 'Arabie Saoudite', flag: '🇸🇦', unit: 'km', currency: 'SAR' },
  { code: 'IT', name: 'Italie', flag: '🇮🇹', unit: 'km', currency: 'EUR' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', unit: 'km', currency: 'EUR' },
];

const Register = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    profession: '',
    language: 'fr',
    country: 'FR',
    distance_unit: 'km',
  });

  const handleProfessionSelect = (professionId) => {
    setFormData({ ...formData, profession: professionId });
  };

  const handleCountrySelect = (country) => {
    setFormData({ 
      ...formData, 
      country: country.code,
      distance_unit: country.unit 
    });
  };

  const handleNext = () => {
    if (!formData.profession) {
      toast.error('Veuillez sélectionner votre activité');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Email et mot de passe sont obligatoires');
      return;
    }

    setLoading(true);

    const result = await register(formData);
    
    if (!result.success) {
      toast.error(result.error || 'Erreur lors de l\'inscription');
    }
    
    setLoading(false);
  };

  const selectedCountry = COUNTRIES.find(c => c.code === formData.country);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <Link to="/" className="inline-flex items-center gap-3 group">
              <div className="w-12 h-12 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Route className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-semibold">OptiTrajet</span>
            </Link>
          </div>
          
          <div className="max-w-md">
            <h2 className="text-4xl font-semibold tracking-tight mb-4">
              Rejoignez les professionnels de la route
            </h2>
            <p className="text-white/80 text-lg">
              Créez votre compte en 30 secondes et commencez à gérer votre activité efficacement.
            </p>
            
            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3 text-white/90">
                <Check className="w-5 h-5 text-white/70" />
                <span>7 jours d'essai gratuit</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <Check className="w-5 h-5 text-white/70" />
                <span>Aucune carte bancaire requise</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <Check className="w-5 h-5 text-white/70" />
                <span>Annulation à tout moment</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-8 text-white/60 text-sm">
            <span>© 2026 OptiTrajet</span>
            <a href="#" className="hover:text-white transition-colors">Confidentialité</a>
            <a href="#" className="hover:text-white transition-colors">CGU</a>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-lg"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
                <Route className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-semibold text-foreground">OptiTrajet</span>
            </Link>
          </div>

          {/* Back Button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <Card className="shadow-card border-border/50">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-semibold">
                    {step === 1 ? 'Votre activité' : 'Vos informations'}
                  </CardTitle>
                  <CardDescription>
                    {step === 1 ? 'Sélectionnez votre domaine' : 'Étape 2/2 — Finalisez votre inscription'}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className={`w-3 h-3 rounded-full transition-colors ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
                  <div className={`w-3 h-3 rounded-full transition-colors ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <AnimatePresence mode="wait">
                {step === 1 ? (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-4"
                  >
                    {/* Profession selection */}
                    <div className="grid grid-cols-2 gap-3">
                      {PROFESSIONS.map((prof) => (
                        <motion.button
                          key={prof.id}
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleProfessionSelect(prof.id)}
                          className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                            formData.profession === prof.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50 hover:bg-muted/50'
                          }`}
                          data-testid={`profession-${prof.id}`}
                        >
                          {formData.profession === prof.id && (
                            <div className="absolute top-2 right-2">
                              <Check className="w-5 h-5 text-primary" />
                            </div>
                          )}
                          <prof.icon className={`w-8 h-8 mb-2 ${
                            formData.profession === prof.id ? 'text-primary' : 'text-muted-foreground'
                          }`} />
                          <p className="font-medium text-foreground">{prof.label}</p>
                          <p className="text-xs text-muted-foreground">{prof.description}</p>
                        </motion.button>
                      ))}
                    </div>

                    {/* Country selection */}
                    <div className="pt-4">
                      <Label className="text-sm font-medium mb-2 block">
                        Votre pays
                      </Label>
                      <div className="grid grid-cols-4 gap-2">
                        {COUNTRIES.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => handleCountrySelect(country)}
                            className={`p-2 rounded-lg border text-center transition-all ${
                              formData.country === country.code
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50'
                            }`}
                            data-testid={`country-${country.code}`}
                          >
                            <span className="text-2xl">{country.flag}</span>
                            <p className="text-xs mt-1 text-muted-foreground">{country.code}</p>
                          </button>
                        ))}
                      </div>
                      {selectedCountry && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Unité par défaut : <strong>{selectedCountry.unit === 'miles' ? 'Miles' : 'Kilomètres'}</strong>
                        </p>
                      )}
                    </div>

                    <Button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-primary hover:bg-primary/90 shadow-button-primary mt-4"
                      disabled={!formData.profession}
                      data-testid="register-next-btn"
                    >
                      Continuer
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-testid="register-form"
                  >
                    {/* Selected profession badge */}
                    <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                      {PROFESSIONS.find(p => p.id === formData.profession)?.icon && (
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          {React.createElement(PROFESSIONS.find(p => p.id === formData.profession).icon, {
                            className: "w-5 h-5 text-primary"
                          })}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">
                          {PROFESSIONS.find(p => p.id === formData.profession)?.label}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {selectedCountry?.flag} {selectedCountry?.name} • {selectedCountry?.unit === 'miles' ? 'Miles' : 'Km'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="text-xs text-primary hover:underline"
                      >
                        Modifier
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">Prénom</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                          <Input
                            id="first_name"
                            type="text"
                            placeholder="Jean"
                            className="pl-10"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            data-testid="register-first-name"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Nom</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                          <Input
                            id="last_name"
                            type="text"
                            placeholder="Dupont"
                            className="pl-10"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            data-testid="register-last-name"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="exemple@email.com"
                          className="pl-10"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="register-email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Mot de passe *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          data-testid="register-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">Minimum 6 caractères</p>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setStep(1)}
                        className="flex-1"
                      >
                        Retour
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-primary hover:bg-primary/90 shadow-button-primary"
                        disabled={loading}
                        data-testid="register-submit-btn"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Création...
                          </>
                        ) : (
                          "Créer mon compte"
                        )}
                      </Button>
                    </div>

                    {/* Trial info */}
                    <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800/50 text-center">
                      <p className="text-sm text-emerald-800 dark:text-emerald-200">
                        <strong>7 jours d'essai gratuit</strong> inclus
                      </p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">Accès complet à toutes les fonctionnalités</p>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              <div className="mt-6 text-center text-sm border-t border-border/50 pt-4">
                <span className="text-muted-foreground">Déjà un compte ? </span>
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Se connecter
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Legal footer */}
          <p className="text-center text-xs text-muted-foreground mt-6">
            En créant un compte, vous acceptez nos{' '}
            <a href="#" className="underline hover:text-foreground">Conditions d'utilisation</a> et{' '}
            <a href="#" className="underline hover:text-foreground">Politique de confidentialité</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
